import { Component, OnInit, OnDestroy, Directive, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Subscription, BehaviorSubject, interval, Subject } from 'rxjs';

import { AppConstants } from 'appshared/app-constants';
import * as Models from 'appshared/shared-models';
import { MiscTools } from 'appshared/misc-tools';
import { ValidationTools } from 'appshared/validation-tools';
import { TextTools } from 'appshared/text-tools';
import { UITools } from 'appshared/ui-tools';
import { SharedLicenseTools } from 'appshared/shared-license-tools';

import { PartnerAdminService } from '../partner-admin.service';
import { OrganizationsService } from '../../organizations/organizations.service';
import { AuthService } from 'client/app/services/auth.service';

import { PagingSearchBarComponent } from 'client/app/components/shared/paging-search-bar/paging-search-bar.component';

@Component({
	selector: 'app-partner-list',
	templateUrl: './partner-list.component.html',
	styleUrls: ['./partner-list.component.scss']
})
export class PartnerListComponent implements OnInit, OnDestroy {
	ac = AppConstants;
	tt = TextTools;
	mt = MiscTools;
	ut = UITools;

	@ViewChild(PagingSearchBarComponent) pagingSearchBar: PagingSearchBarComponent = null;

	now = new Date();

	// 'standard' list stuff
	loading = true;
	refreshing = false;

	items: Models.Partnership[] = [];
	itemsToShow: Models.Partnership[] = [];

	// other stuff
	private userSubscription: Subscription;
	canAdd = false;

	organizations: Models.Organization[] = [];

	// new table stuff
	displayOptions: Models.TableDisplayOptions = new Models.TableDisplayOptions();
	pagingOptions: Models.TablePagingOptions = new Models.TablePagingOptions();
	storageKey: string = 'WIPE-FILTER.cpcp-partners-list';

	showDisabledStorageKey: string = 'CLEAN.cp-partners-list.showDisabled';
	showDisabled: boolean = false;

	fieldsToShow: string[] = [];
	allFieldsToShow: string[] = [];
	lockedFields: string[] = [];
	baselineFields: string[] = [];
	minimalFields: string[] = [];

	columnDefs: Models.ColumnDefinition[] = [
		{
			field: 'is_listed',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Listed/Hidden',
			icon: 'power-off'
		}, {
			field: '__organization',
			type: 'baseText',
			sortType: 'text',
			label: 'Organization',
			toolTip: null,
			icon: null
		}, {
			field: 'name',
			type: 'baseText',
			sortType: 'text',
			label: 'Name',
			toolTip: null,
			icon: null
		}, {
			field: '__status',
			type: 'baseText',
			sortType: 'text',
			label: 'Status',
			toolTip: null,
			icon: null
		}, {
			field: '__dtype',
			type: 'baseText',
			sortType: 'text',
			label: 'Integration Type',
			toolTip: null,
			icon: null
		}, {
			field: '__itype',
			type: 'baseText',
			sortType: 'text',
			label: 'SDK Type',
			toolTip: null,
			icon: null
		}, {
			field: '__zm_certified',
			type: 'baseText',
			sortType: 'text',
			label: '',
			toolTip: 'ZEN Master Certified',
			icon: AppConstants.sectionIcons.zencustomers
		}, {
			field: '__accept_mode',
			type: 'baseText',
			sortType: 'text',
			label: 'Accept Mode',
			toolTip: '',
			icon: null
		}, {
			field: 'added_on',
			type: 'baseDate',
			sortType: 'numeric',
			label: null,
			toolTip: 'Days Since Added',
			icon: 'clock'
		}, {
			field: 'edited_on',
			type: 'baseDate',
			sortType: 'numeric',
			label: null,
			toolTip: 'Days Since Updated',
			icon: 'pencil'
		}
	];

	constructor(
		private partnerAdminService: PartnerAdminService,
		private organizationsService: OrganizationsService,
		private authService: AuthService,
	) { }

	// ------------------------------------------------------------------------
	ngOnInit(): void {
		this.loading = true;

		this.userSubscription = this.authService.user.subscribe(authUser => {
			this.canAdd = (authUser && ValidationTools.checkAccess(authUser, 'manage-partnerships'));

			if (localStorage.getItem(this.showDisabledStorageKey))
				this.showDisabled = localStorage.getItem(this.showDisabledStorageKey) === 'yes';

			this.loadData();
		});
	}

	// ------------------------------------------------------------------------
	ngOnDestroy() {
		if (this.userSubscription) this.userSubscription.unsubscribe();
	}

	// ------------------------------------------------------------------------
	async loadData(refresh: boolean = false) {
		this.loading = true;

		if (refresh)
			await this.partnerAdminService.refreshAll().toPromise();

		this.organizations = this.organizationsService.getAll();

		const partners: Models.Partnership[] = this.partnerAdminService.getAll();
		this.items = [];
		for (const item of partners) {
			if (item.is_listed === 1 || this.showDisabled) {
				item['__organization'] = this.getOrgName(item.org_id);
				item['__status'] = item.extras.status;
				item['__dtype'] = item.extras.dtype;
				item['__itype'] = item.extras.itype;
				item['__accept_mode'] = item.extras.accept_mode;
				item['__zm_certified'] = item.extras.zm_certified ? 'yes' : 'no';
				this.items.push(item);
			} // if
		} // for

		this.fieldsToShow = [];
		this.allFieldsToShow = [];
		this.lockedFields = [];
		this.baselineFields = [];
		this.minimalFields = [];
		for (const cd of this.columnDefs)
			this.fieldsToShow.push(cd.field);

		this.loading = false;

		await MiscTools.delay(100);
		if (this.pagingSearchBar)
			this.pagingSearchBar.setupPaging(this.items, this.columnDefs, this.storageKey, 'name', 'asc');

	}

	// ------------------------------------------------------------------------
	getOrgName(id: number) {
		const idx = MiscTools.findIndex(this.organizations, id);
		if (idx !== -1) return this.organizations[idx].name
		return '';
	}

	// ------------------------------------------------------------------------
	async toggleShowDisabled() {
		this.showDisabled = !this.showDisabled;
		localStorage.setItem(this.showDisabledStorageKey, this.showDisabled ? 'yes' : 'no');
		await this.loadData();
	}

	// ------------------------------------------------------------------------
	filterItems(matchWords: string[]): any[] {
		const tmpList: Models.Partnership[] = [];
		for (const item of this.items) {
			let match: boolean = true;

			if (matchWords.length > 0) {
				let matches: number = 0;
				for (const w of matchWords) {
					if (item.name.toLowerCase().includes(w)
						|| (item.extras.status && item.extras.status.toLowerCase().includes(w))
						|| (item.extras.dtype && item.extras.dtype.toLowerCase().includes(w))
						|| (item.extras.itype && item.extras.itype.toLowerCase().includes(w))
						|| (item['__organization'] && item['__organization'].toLowerCase().includes(w))
					)
						matches++;
				} // for
				if (matches !== matchWords.length) match = false;
			} // if

			if (match) tmpList.push(item);
		} // for
		return tmpList;
	}

	// ------------------------------------------------------------------------
	setSortBy(field: string) {
		this.pagingSearchBar.setSortBy(field);
	} // setSortBy

	// ------------------------------------------------------------------------
	// Catch calls from the paging/search bar
	// ------------------------------------------------------------------------
	getParentMethod(): any {
		return {
			filterItems: (matchWords: string[]) => {
				return this.filterItems(matchWords);
			},
			updateItemsToShow: (itemsToShow: any[], displayOptions: Models.TableDisplayOptions, pagingOptions: Models.TablePagingOptions) => {
				this.itemsToShow = itemsToShow;
				this.displayOptions = displayOptions;
				this.pagingOptions = pagingOptions;
			},
			updateFieldsToShow: (fieldsToShow: string[]) => {
				this.fieldsToShow = fieldsToShow;
			},
			setPageNum: (pageNum: number, uniqueId: string) => {
				if (this.pagingSearchBar) this.pagingSearchBar.setPageNum(pageNum);
			},
			setSortBy: (field: string) => {
				this.setSortBy(field);
			}
		}
	}

}
